import { type RefObject, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { ActiveTrainState, TrainRenderData } from '@/types';

import { CLEANUP_CHECK_INTERVAL_MS } from '@/utils/constants/map';
import {
  SEARCH_PARAM_DRAWER,
  SEARCH_PARAM_LOCK_PIN,
  SEARCH_PARAM_LOCK_PIN_SECTION_ID,
  SEARCH_PARAM_LOCK_PIN_TYPE,
  SEARCH_PARAM_ROUTES,
  SEARCH_PARAM_SHOW,
  SEARCH_PARAMS_ROUTE_ID,
} from '@/utils/constants/queries';

const clearLockedTrainParams = (
  prev: URLSearchParams,
  journeyId?: string | number | null
): URLSearchParams => {
  const newParams = new URLSearchParams(prev);
  newParams.delete(SEARCH_PARAM_LOCK_PIN);
  newParams.delete(SEARCH_PARAM_LOCK_PIN_TYPE);
  newParams.delete(SEARCH_PARAM_LOCK_PIN_SECTION_ID);
  newParams.delete(SEARCH_PARAM_DRAWER);

  if (newParams.get(SEARCH_PARAM_SHOW) === SEARCH_PARAM_ROUTES) {
    newParams.delete(SEARCH_PARAM_SHOW);
  }

  if (journeyId && newParams.get(SEARCH_PARAMS_ROUTE_ID) === String(journeyId)) {
    newParams.delete(SEARCH_PARAMS_ROUTE_ID);
  }

  return newParams;
};

export const useLockedTrainCleanup = (
  lockedJourneyId: string | number | null,
  trainData: TrainRenderData[],
  isSocketReady: boolean,
  trainStateMapRef: RefObject<Map<string, ActiveTrainState>>
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const hasSeenLockedTrainRef = useRef(false);
  const prevLockedIdRef = useRef<string | number | null>(lockedJourneyId);
  const isTrainPinned = searchParams.get(SEARCH_PARAM_LOCK_PIN_TYPE) === 'route';

  const trainDataRef = useRef<TrainRenderData[]>(trainData);

  useEffect(() => {
    trainDataRef.current = trainData;
  }, [trainData]);

  useEffect(() => {
    hasSeenLockedTrainRef.current = false;
  }, [lockedJourneyId]);

  // Handle Explicit Un-pin by the user
  useEffect(() => {
    if (!lockedJourneyId && prevLockedIdRef.current !== null && isTrainPinned) {
      setSearchParams((prev) => clearLockedTrainParams(prev, prevLockedIdRef.current), {
        replace: true,
      });
    }

    prevLockedIdRef.current = lockedJourneyId;
  }, [lockedJourneyId, isTrainPinned]);

  // Handle train naturally disappearing/arriving
  useEffect(() => {
    if (!lockedJourneyId) {
      return;
    }

    const checkInterval = setInterval(() => {
      const isLockedTrainActive = trainDataRef.current.some((t) => t.id === lockedJourneyId);

      if (isLockedTrainActive) {
        hasSeenLockedTrainRef.current = true;
      }

      if (hasSeenLockedTrainRef.current && !isLockedTrainActive) {
        setSearchParams((prev) => clearLockedTrainParams(prev, lockedJourneyId), { replace: true });
        hasSeenLockedTrainRef.current = false;
        clearInterval(checkInterval);
      }
    }, CLEANUP_CHECK_INTERVAL_MS);

    return () => clearInterval(checkInterval);
  }, [lockedJourneyId]);

  // Handle Dead on Arrival (User refreshed, but the train finished its route while they were away)
  useEffect(() => {
    if (isSocketReady && lockedJourneyId && !hasSeenLockedTrainRef.current && isTrainPinned) {
      const isCurrentlyActive = trainStateMapRef.current?.has(String(lockedJourneyId));

      if (!isCurrentlyActive) {
        setSearchParams((prev) => clearLockedTrainParams(prev, lockedJourneyId), { replace: true });
      }
    }
  }, [isSocketReady, lockedJourneyId, isTrainPinned, trainStateMapRef]);
};
